// ChangesOrdersPage.js
import React, { useState, useEffect } from "react";
import { useOrderChangesDialog } from "../../Components/orders/useOrderChangesDialog";
import OrderChangeImagesButton from "../../Components/orders/OrderChangeImagesButton";
import { useQuery } from "@tanstack/react-query";
import { fetchChangesOrders, updateOrderStatus, updateOrderStatusBulk, deleteOrder, fetchChangesOrdersTotals, fetchChangesOrdersSummary } from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { readyOrderUnits,formatPhoneWithCaseStyle,
  caseStyleNameFromDetail,
  teePlusPackedItemData,
  orderMatchesCaseStyleFilter,
  CASE_STYLE_FILTER_ALL,
  detailHasPersonalizedName,
  namedOrderRowClass,
  namedItemBoxClass,
} from "../../utils/orderLineDisplay";
import { NamedPieceBadge, OrderNameMark } from "../../Components/orders/NamedPieceBadge";
import CaseStyleOrderFilter from "../../Components/orders/CaseStyleOrderFilter";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { downloadCustomFile } from "../../api/FileAPIs";
import EditOrderModal from "./EditOrderModal";
import MergeOrdersModal from "./MergeOrdersModal";
import { copyOrderToTeePlus } from "../../utils/productionSource";
import {
  ChevronRight,
  ChevronLeft,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  DollarSign,
  Hash,
  FileText,
  Copy,
  Download,
  Link as LinkIcon,
  Edit,
  Trash2,
  Archive,
  Clock,
  CheckCircle,
  AlertTriangle,
  ShoppingCart,
  MessageSquare,
  Truck,
  AlertCircle,
  Layers,
  CopyCheck,
  Filter,
  Save,
  Plus,
  Search,
  Loader,
  XCircle,
  RotateCcw,
  Printer,
  Send,
  Shirt,
  Merge,
  CheckSquare,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const ChangesOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const { changesDialog, openOrderChanges } = useOrderChangesDialog(async (savedOrderId) => {
    removeOrderFromView(savedOrderId);
    toast.success("تم تحديث الطلب إلى Changes بنجاح");
  });
  
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [caseStyleFilter, setCaseStyleFilter] = useState(CASE_STYLE_FILTER_ALL);
  const [showFilters, setShowFilters] = useState(false);
  const [designFilter, setDesignFilter] = useState("all"); // "all" | "custom" | "normal"
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [mergingOrder, setMergingOrder] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const { data: totals, isLoading: isLoadingTotals, error: errorTotals } = useQuery({
    queryKey: ["changesOrdersTotals"],
    queryFn: fetchChangesOrdersTotals,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { totalProfit: 0, totalAmount: 0, totalProductsSold: 0 },
  });

  const { data: changesOrdersSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["changesOrdersSummary"],
    queryFn: fetchChangesOrdersSummary,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const isLoading = isLoadingTotals || isLoadingSummary;
  const queryError = errorTotals || errorSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {
        // toast.success("تم النسخ بنجاح!");
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        // toast.success("تم النسخ بنجاح!");
      } else {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const readyUnits = await readyOrderUnits(detail);
          if (readyUnits) {
            enrichedItems.push(...readyUnits);
            continue;
          }
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            phoneType: type.name || "",
            caseStyleName: caseStyleNameFromDetail(detail),
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: design.code.startsWith("CUSTOM") 
              ? `${API_BASE_URL_V2}${design.driveLink}` 
              : design.driveLink || "",
            code: design.code || "None",
            customNameAr: detail.customNameAr || detail.CustomNameAr || "",
            customNameEn: detail.customNameEn || detail.CustomNameEn || "",
            hasPersonalizedName: detailHasPersonalizedName(detail),
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "غير معروف",
          state: order.address?.state || "غير معروف",
          city: order.address?.city || "",
          phoneNumber: order.phoneNumber || "غير معروف",
          phoneNumber2: order.phoneNumber2 || "غير معروف",
          name: order.name || "غير معروف",
          note: order.note || "",
          changeNoteImageCount: Number(order.changeNoteImageCount || 0),
          status: order.status || "غير معروف",
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchChangesOrders(0, pageSize);
      setHasMore(data.length === pageSize);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchChangesOrders(currentPage, pageSize);
      setHasMore(data.length === pageSize);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setIsOrdersLoading(true);
    try {
      const data = await fetchChangesOrders(0, newSize);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setSelectedOrderNumbers(new Set());
      setCurrentPage(1);
      setHasMore(data.length === newSize);
    } catch (err) {
      console.error("Error changing page size:", err);
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const removeOrderFromView = (orderId) => {
    setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
    setSelectedOrderNumbers((prev) => {
      if (!prev.has(orderId)) return prev;
      const next = new Set(prev);
      next.delete(orderId);
      return next;
    });
  };

  const handleUpdateStatus = async (orderId, newStatus, _order) => {
    if (!canAct) return;
    let reason = "no reason (normal order)";

    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);
        removeOrderFromView(orderId);
        toast.success("تم تحديث الطلب إلى قديم!");

      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Changes") {
      openOrderChanges(orderId);
      return;
    } else if (newStatus === "Spam") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Incomplete") {
      const note = window.prompt(
        "ملاحظة Incomplete (اختياري) — السبب أو نوع الجراب الناقص:\nاتركه فاضي لو مش عايز تكتب حاجة"
      );
      if (note === null) return;
      reason = note.trim() || "no reason (normal order)";
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      removeOrderFromView(orderId);
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      removeOrderFromView(orderId);
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleOrderEdited = async (updatedOrder) => {
    if (!updatedOrder?.orderId) {
      await loadInitialOrders();
      return;
    }
    try {
      const [enriched] = await enrichOrders([updatedOrder]);
      setLoadedOrders((prev) =>
        prev.map((o) => (o.orderNumber === enriched.orderNumber ? enriched : o))
      );
    } catch (err) {
      console.error(err);
      await loadInitialOrders();
    }
  };

  const handleOrdersMerged = async (updatedOrder, otherOrderId) => {
    try {
      const [enriched] = await enrichOrders([updatedOrder]);
      setLoadedOrders((prev) =>
        prev
          .filter((o) => o.orderNumber !== otherOrderId)
          .map((o) => (o.orderNumber === enriched.orderNumber ? enriched : o))
      );
      setSelectedOrderNumbers((prev) => {
        if (!prev.has(otherOrderId)) return prev;
        const next = new Set(prev);
        next.delete(otherOrderId);
        return next;
      });
    } catch (err) {
      console.error(err);
      await loadInitialOrders();
    }
  };

  const handleCopyOrderInfo = (order) => {
    const phone = `${order.phoneNumber}${order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""}`;
    const formattedState = `${order.state} (D)`;
    const text = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD`;
    copyToClipboard(text);
  };

  const handleCopyItems = (order) => {
    const lines = order.items.map((item, index) => {
      const itemNum = `${index + 1}-${order.items.length}`;
      const type = item.type || "Unknown Type";
      const driveLink = item.driveLink || "No Link";
      return `${itemNum}\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
    }).join('\n');
    copyToClipboard(lines);
  };

    const handleCopyAllExcelDataNoReference = async (order) => {
    try {
      // Format phone number
      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""
      }`;
      
      const formattedState = `${order.state} (D)`;
      
      // No reference field - using empty string or tab
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const itemData = teePlusPackedItemData(item);
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join('\n');
      await copyOrderToTeePlus(finalText, order.orderNumber);
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      if (err.code === "SOURCE_FAILED") {
        toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      } else {
        toast.error("فشل في النسخ، حاول مرة تانية");
      }
    }
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  const handleCopyBostaMessage = () => {
    const message = `
بنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅
تنويه مهم:
في رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  

كمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾
يُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدء الشحن ✉️⚡  

شكرًا لاختيارك جرابتكم 🌷`;
    copyToClipboard(message);
  };



  const toggleSelectOrder = (orderNumber) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const setSelectAllVisible = (checked, visibleOrders) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (checked) visibleOrders.forEach((o) => next.add(o.orderNumber));
      else visibleOrders.forEach((o) => next.delete(o.orderNumber));
      return next;
    });
  };

  const handleSendSelectedToPrintingHold = async () => {
    if (!isAdmin(authState.role)) return;
    const selected = loadedOrders.filter((o) => selectedOrderNumbers.has(o.orderNumber));
    if (selected.length === 0) {
      toast.info("اختر أوردرات أولاً");
      return;
    }
    const confirmSend = window.confirm(
      `تحويل ${selected.length} طلب(ات) محددة إلى Printing Hold؟\n\nالطلبات المحددة ستختفي من الصفحة فورًا.`
    );
    if (!confirmSend) return;

    setIsBulkUpdating(true);
    try {
      const ids = selected.map((o) => o.orderNumber);
      const result = await updateOrderStatusBulk(ids, "Printing Hold", "تم التحويل الجماعي إلى Printing Hold");
      const okIds = new Set(
        (result.results || []).filter((r) => r.ok).map((r) => r.orderId)
      );
      const failed = (result.results || []).filter((r) => !r.ok);
      setLoadedOrders((prev) => prev.filter((o) => !okIds.has(o.orderNumber)));
      setSelectedOrderNumbers((prev) => {
        const next = new Set(prev);
        okIds.forEach((id) => next.delete(id));
        return next;
      });
      toast.success(`تم تحديث ${okIds.size} طلب(ات) إلى Printing Hold`);
      failed.slice(0, 5).forEach((r) =>
        toast.error(`#${r.orderId}: ${typeof r.error === "string" ? r.error : "فشل التحديث"}`)
      );
    } catch (err) {
      toast.error(err.message || "فشل التحديث الجماعي");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const isOrderCustom = (order) =>
    order.items.some((item) => item.code && item.code.startsWith("CUSTOM"));

  const isOrderNonCustom = (order) => !isOrderCustom(order);

  const filteredOrders = loadedOrders.filter((order) => {
    if (!orderMatchesCaseStyleFilter(order, caseStyleFilter)) return false;
    if (designFilter === "custom" && !isOrderCustom(order)) return false;
    if (designFilter === "normal" && !isOrderNonCustom(order)) return false;

    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      {changesDialog}
      <ToastContainer position="top-center" />

      {editingOrderId && (
        <EditOrderModal
          orderId={editingOrderId}
          onClose={() => setEditingOrderId(null)}
          onSaved={handleOrderEdited}
        />
      )}

      {mergingOrder && (
        <MergeOrdersModal
          sourceOrder={mergingOrder}
          onClose={() => setMergingOrder(null)}
          onMerged={handleOrdersMerged}
        />
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <RotateCcw className="text-purple-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              طلبات تحت التغيير
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {/* Stats Cards */}
        {isAdmin(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Package className="text-purple-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص طلبات التغيير</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-purple-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-purple-600">{changesOrdersSummary.count} طلبات</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{changesOrdersSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-indigo-100 rounded-xl">
                  <Layers className="text-indigo-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">إجمالي المنتجات</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="productsCard" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-xl">
                    <span className="text-gray-600">عدد المنتجات</span>
                    <span className="text-2xl font-bold text-indigo-600">{totals.totalProductsSold} قطعه</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Message Buttons */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <MapPin className="text-amber-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900 text-lg">طلب توضيح العنوان</h3>
                  <p className="text-sm text-amber-700">إذا كان العنوان غير واضح</p>
                </div>
              </div>
              <button
                onClick={handleCopyAddressClarification}
                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Truck className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-900 text-lg">رسالة بوسطة</h3>
                  <p className="text-sm text-blue-700">لشركة الشحن</p>
                </div>
              </div>
              <button
                onClick={handleCopyBostaMessage}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>
        </div> */}

        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-6 py-3 border-2 rounded-xl transition-all ${
                showFilters || designFilter !== "all"
                  ? "bg-purple-50 border-purple-400 text-purple-700"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Filter size={20} />
              تصفية
              {designFilter !== "all" && (
                <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">1</span>
              )}
            </button>
          </div>

          <CaseStyleOrderFilter
            value={caseStyleFilter}
            onChange={setCaseStyleFilter}
            orders={loadedOrders}
            accent="purple"
          />

          {showFilters && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
              <span className="text-sm text-gray-600 ml-2">نوع التصميم:</span>
              {[
                { value: "all", label: "كل الطلبات" },
                { value: "custom", label: "طلبات مخصصة (CUSTOM)" },
                { value: "normal", label: "طلبات عادية" },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setDesignFilter(value)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    designFilter === value
                      ? value === "custom"
                        ? "bg-green-600 text-white shadow-md"
                        : value === "normal"
                          ? "bg-gray-600 text-white shadow-md"
                          : "bg-purple-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <label className="font-medium text-gray-700">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-400 transition-all bg-white"
                disabled={isOrdersLoading || isBulkUpdating}
              >
                <option value={10}>10 طلبات</option>
                <option value={20}>20 طلب</option>
                <option value={30}>30 طلب</option>
                <option value={40}>40 طلب</option>
                <option value={50}>50 طلب</option>
                <option value={80}>80 طلب</option>
                <option value={100}>100 طلب</option>
              </select>
              <span className="text-2xl text-gray-600">(إجمالي المعروض: {loadedOrders.length})</span>
              {isAdmin(authState.role) && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectAllVisible(true, filteredOrders)}
                    disabled={filteredOrders.length === 0 || isBulkUpdating}
                    className="bg-white border-2 border-purple-300 text-purple-800 font-bold px-4 py-2 rounded-xl hover:bg-purple-50 disabled:opacity-50 flex items-center gap-2"
                  >
                    <CheckSquare size={16} />
                    تحديد الظاهر
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectAllVisible(false, filteredOrders)}
                    disabled={selectedOrderNumbers.size === 0 || isBulkUpdating}
                    className="bg-white border-2 border-gray-300 text-gray-700 font-bold px-4 py-2 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                  >
                    إلغاء التحديد
                  </button>
                </div>
              )}
            </div>
            {isAdmin(authState.role) && (
              <button
                type="button"
                onClick={handleSendSelectedToPrintingHold}
                disabled={isOrdersLoading || isBulkUpdating || selectedOrderNumbers.size === 0}
                className="bg-slate-700 hover:bg-slate-800 disabled:bg-gray-400 text-white font-bold px-5 py-3 rounded-xl shadow-lg disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isBulkUpdating ? <Loader className="animate-spin" size={18} /> : <Printer size={18} />}
                إرسال كل المتحدد إلى Printing Hold ({selectedOrderNumbers.size})
              </button>
            )}
            {isOrdersLoading && (
              <div className="flex items-center gap-2 text-purple-600">
                <Loader className="animate-spin" size={20} />
                <span>جاري التحميل...</span>
              </div>
            )}
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  <table className="w-full min-w-[1200px] table-auto">
                    <thead>
                      <tr className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
                        <th className="p-4 text-right font-bold text-purple-800">
                          <div className="flex items-center gap-2">
                            {isAdmin(authState.role) && (
                              <input
                                type="checkbox"
                                className="w-5 h-5 accent-purple-600"
                                checked={
                                  filteredOrders.length > 0 &&
                                  filteredOrders.every((o) => selectedOrderNumbers.has(o.orderNumber))
                                }
                                onChange={(e) => setSelectAllVisible(e.target.checked, filteredOrders)}
                                disabled={filteredOrders.length === 0 || isBulkUpdating}
                              />
                            )}
                            <span>رقم الطلب</span>
                          </div>
                        </th>
                        <th className="p-4 text-right font-bold text-purple-800">العميل</th>
                        <th className="p-4 text-right font-bold text-purple-800">العنوان</th>
                        <th className="p-4 text-center font-bold text-purple-800">الإجمالي</th>
                        <th className="p-4 text-right font-bold text-purple-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-purple-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-purple-800">نسخ</th>
                        <th className="p-4 text-center font-bold text-purple-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-purple-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className={namedOrderRowClass(order)}>
                          {/* Order Number */}
                          <td className="p-4">
                            <div className="flex flex-col items-start gap-1">
                              {isAdmin(authState.role) && (
                                <input
                                  type="checkbox"
                                  className="w-5 h-5 accent-purple-600"
                                  checked={selectedOrderNumbers.has(order.orderNumber)}
                                  onChange={() => toggleSelectOrder(order.orderNumber)}
                                  disabled={isBulkUpdating}
                                />
                              )}
                              <span className="font-mono font-bold text-purple-600">#{order.orderNumber}</span>
                              <OrderNameMark order={order} />
                            </div>
                          </td>

                          {/* Customer Name */}
                          <td className="p-4">
                            {(isAdmin(authState.role) || canAct) && (
                              <div className="flex items-center gap-2 mb-5">
                                {canAct && (
                                  <button
                                    onClick={() => setEditingOrderId(order.orderNumber)}
                                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-all"
                                  >
                                    <div className="flex items-center justify-center gap-1">
                                      <Edit size={14} />
                                      تعديل
                                    </div>
                                  </button>
                                )}
                                {canAct && (
                                  <button
                                    onClick={() => setMergingOrder(order)}
                                    className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium transition-all"
                                  >
                                    <div className="flex items-center justify-center gap-1">
                                      <Merge size={14} />
                                      دمج
                                    </div>
                                  </button>
                                )}
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span className="font-medium">{order.name}</span>
                            </div>

                          {/* Phone Numbers */}
                          <br />
                          <div className="flex items-center gap-2">
                          <Phone size={16} className="text-green-600" />

                          <div className="flex flex-col">
                            <span
                              onClick={() => {
                                navigator.clipboard.writeText(order.phoneNumber);
                                toast.success(`تم نسخ الرقم: ${order.phoneNumber}`);
                              }}
                              className="text-green-600 hover:underline cursor-pointer"
                              title="نسخ الرقم"
                            >
                              {order.phoneNumber}
                            </span>

                            {order.phoneNumber2 !== "غير معروف" && (
                              <span
                                onClick={() => {
                                  navigator.clipboard.writeText(order.phoneNumber2);
                                  toast.success(`تم نسخ الرقم: ${order.phoneNumber2}`);
                                }}
                                className="text-green-600 hover:underline cursor-pointer"
                                title="نسخ الرقم"
                              >
                                {order.phoneNumber2}
                              </span>
                            )}
                          </div>
                        </div>
                          </td>

                          {/* Address */}
                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-start gap-2">
                              <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                              <span style={{fontFamily : "cairo black"}} className="text-[13px] line-clamp-5">{order.address}</span>
                            </div>
                            <br />
                             <div className="flex items-center gap-2">
                              <Map size={16} className="text-gray-500" />
                              <span className="text-2xl text-purple-600">{order.state}</span>
                            </div>
                          </td>

                         

                          {/* Total */}
                          <td className="p-4 text-center">
                            <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                          </td>

                          {/* Products + Drive Links - Merged */}
                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className={namedItemBoxClass(item)}>
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <div className="font-medium text-purple-700">{item.name}</div>
                                    <NamedPieceBadge item={item} className="mt-1" />
                                    <div style={{fontFamily:"cairo black"}} className="text-[13px] text-black-600">{item.type}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      {item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-green-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                       {!item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                      
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-purple-600 hover:text-purple-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          
                                          {item.code.startsWith("CUSTOM") && (
                                            <button
                                              onClick={() =>
                                                handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)
                                              }
                                              className="flex items-center gap-2 text-white hover:text-white transition-transform hover:scale-110 px-3 py-2"
                                              title="تحميل الصورة"
                                              style={{
                                                backgroundColor: "#15d225",
                                                borderRadius: "4px",
                                              }}
                                            >
                                              <Download size={16} />
                                              <span className="text-sm">Download</span>
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {index < order.items.length - 1 && (
                                  <hr className="my-2 border-gray-200" />
                                )}
                              </div>
                            ))}
                          </td>

                          {/* Notes */}
                          <td className="p-4 max-w-[150px]">
                            <p style={{fontFamily:"cairo black"}} className="text-[16px]">{order.note || "—"}</p>
                            <OrderChangeImagesButton orderId={order.orderNumber} count={order.changeNoteImageCount} />
                          </td>

                          {/* Copy Buttons */}
                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleCopyOrderInfo(order)}
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={14} />
                                العميل
                              </button>
                              <button
                                onClick={() => handleCopyItems(order)}
                                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-bold flex items-center justify-center gap-2"
                              >
                                <Layers size={14} />
                                المنتجات
                              </button>
                              <button
                                onClick={() => handleCopyAllExcelDataNoReference(order)}
                                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={16} />
                                نسخ (TeePlus)
                              </button>
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="p-2">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              {/* {isAdmin(authState.role) && (
                                <button
                                  onClick={() => setEditingOrderId(order.orderNumber)}
                                  className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-all"
                                >
                                  <div className="flex items-center justify-center gap-1">
                                    <Edit size={14} />
                                    تعديل
                                  </div>
                                </button>
                              )} */}
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                                className="bg-gray-600 text-white px-6 py-4 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                className="bg-yellow-500 text-white  px-6 py-4 rounded-lg hover:bg-yellow-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Clock size={14} />
                                  معلق
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Incomplete")}
                                className="bg-amber-600 text-white px-6 py-4 rounded-lg hover:bg-amber-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Incomplete
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Girabatcom")}
                                className="bg-cyan-500 text-white px-6 py-4 rounded-lg hover:bg-purple-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <RotateCcw size={14} />
                                  Girabatcom
                                </div>
                              </button>
                              {isAdmin(authState.role) && (
                              <>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Printing Hold")}
                                className="bg-slate-700 text-white px-6 py-4 rounded-lg hover:bg-slate-800 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Printer size={14} />
                                  Printing Hold
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Printlet")}
                                className="bg-fuchsia-600 text-white px-6 py-4 rounded-lg hover:bg-fuchsia-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Send size={14} />
                                  Printlet
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "TeePlus")}
                                className="bg-emerald-600 text-white px-6 py-4 rounded-lg hover:bg-emerald-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Shirt size={14} />
                                  Tee Plus
                                </div>
                              </button>
                              </>
                              )}
                              {/* <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Under Purchase")}
                                className="bg-indigo-500 text-white px-3 py-2 rounded-lg hover:bg-indigo-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <ShoppingCart size={14} />
                                  تحت الشراء
                                </div> */}
                              {/* </button> */}
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>

                          {/* Delete */}
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Spam
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <RotateCcw className="mx-auto mb-4 text-gray-400" size={64} />
                  {loadedOrders.length > 0 ? (
                    <>
                      <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات تطابق التصفية</p>
                      <p className="text-lg text-gray-500">جرّب تغيير نوع التصميم أو البحث</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات تحت التغيير</p>
                      <p className="text-lg text-gray-500">سيتم عرض الطلبات التي تحتاج تغييرات هنا</p>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangesOrdersPage;